import type {ReactElement} from 'react';
import React, {useState} from 'react';
import type {SelectedImageAsset} from '@Components/ai-chat-window/ai-chat-window.types';
import {SelectedAssetThumb} from '../selected-asset-thumb/selected-asset-thumb';
import styles from './super-user-image-context-details.module.scss';
import type {SuperUserImageContextDetailsProps} from './super-user-image-context-details.types';

const getAssetCardKey = (asset: SelectedImageAsset, index: number): string => {
  return `${asset.imagePart.type}-${index}`;
};

const getAssetReason = (asset: SelectedImageAsset): ReactElement | null => {
  const reason = asset.selectionReason?.trim() ?? '';
  if (reason.length === 0) {
    return null;
  }
  return <span className={styles.assetReason}>{reason}</span>;
};

const getAssetTag = (label: string, value: string | null | undefined): ReactElement | null => {
  const trimmed = value?.trim() ?? '';
  if (trimmed.length === 0) {
    return null;
  }
  return (
    <span className={styles.assetTag}>
      <span className={styles.assetTagLabel}>{label}</span>
      {trimmed}
    </span>
  );
};

const renderTextSection = (label: string, value: string): ReactElement | null => {
  if (value.length === 0) {
    return null;
  }
  return (
    <div className={'flex-v-row gap-1'}>
      <span className={styles.sectionLabel}>{label}</span>
      <span className={styles.sectionText}>{value}</span>
    </div>
  );
};

const getAssetTags = (asset: SelectedImageAsset): ReactElement | null => {
  const roleTag = getAssetTag('Role', asset.role);
  const shapeTag = getAssetTag('Shape', asset.assetShape);
  const framingTag = getAssetTag('Framing', asset.framing);
  if (roleTag === null && shapeTag === null && framingTag === null) {
    return null;
  }
  return (
    <div className={styles.assetTags}>
      {roleTag}
      {shapeTag}
      {framingTag}
    </div>
  );
};

const getAssetCard = (asset: SelectedImageAsset, index: number): ReactElement => {
  return (
    <div key={getAssetCardKey(asset, index)} className={styles.assetCard}>
      <SelectedAssetThumb asset={asset} />
      {getAssetTags(asset)}
      {getAssetReason(asset)}
    </div>
  );
};

export function SuperUserImageContextDetails({enhancedPrompt, context, useSmartAssetSelection, producedByAgent}: SuperUserImageContextDetailsProps): ReactElement | null {
  const [isExpanded, setIsExpanded] = useState(false);

  const summary = context?.summary?.trim() ?? '';
  const selectedAssets = context?.selectedAssets ?? [];
  const trimmedEnhancedPrompt = enhancedPrompt?.trim() ?? '';
  const mode = context?.mode?.trim() ?? '';
  const trimmedProducedByAgent = producedByAgent?.trim() ?? '';
  const sessionGoal = context?.sessionGoal?.trim() ?? '';
  const sessionProgress = context?.sessionProgress?.trim() ?? '';
  const lastUserSignal = context?.lastUserSignal?.trim() ?? '';

  const smartAssetSelectionEnabled = useSmartAssetSelection === true;
  const hasContent =
    selectedAssets.length > 0 ||
    [trimmedEnhancedPrompt, summary, mode, trimmedProducedByAgent, sessionGoal, sessionProgress, lastUserSignal].some((s) => s.length > 0);

  if (!hasContent) {
    return null;
  }

  const getAssetsToShow = (): ReactElement | null => {
    if (selectedAssets.length === 0) {
      return null;
    }

    return (
      <div className={'flex-v-row gap-1'}>
        <span className={styles.sectionLabel}>Reference images ({selectedAssets.length})</span>
        <div className={`flexbox gap-2 ${styles.assets}`}>{selectedAssets.map(getAssetCard)}</div>
      </div>
    );
  };

  const getSmartAssetSelectionFlag = (): ReactElement => {
    return (
      <div className={'flex-v-row gap-1'}>
        <span className={styles.sectionLabel}>Smart asset selection</span>
        <span className={styles.sectionText}>{smartAssetSelectionEnabled ? 'On' : 'Off'}</span>
      </div>
    );
  };

  const getGenerationContext = (): ReactElement | null => {
    if (!isExpanded) {
      return null;
    }

    return (
      <div className={`flex-v-row gap-3 spacing-m-t-2`}>
        {renderTextSection('Produced by agent', trimmedProducedByAgent)}
        {getSmartAssetSelectionFlag()}
        {renderTextSection('Session goal', sessionGoal)}
        {renderTextSection('Session progress', sessionProgress)}
        {renderTextSection('Last user signal', lastUserSignal)}
        {renderTextSection('Mode', mode)}
        {renderTextSection('Context summary', summary)}
        {renderTextSection('Enhanced prompt', trimmedEnhancedPrompt)}
        {getAssetsToShow()}
      </div>
    );
  };

  return (
    <div className={`radius-8 spacing-p-2 spacing-m-t-2 ${styles.container}`}>
      <button
        type="button"
        className={`_cursor-pointer flexbox flex-align-items-center spacing-p-0 gap-1 ${styles.toggle}`}
        onClick={(): void => {
          setIsExpanded((prev) => !prev);
        }}
        aria-expanded={isExpanded}
      >
        <span className={`${styles.chevron} ${isExpanded ? styles.chevronExpanded : ''}`}>{'▶'}</span>
        <span>Generation Context</span>
      </button>
      {getGenerationContext()}
    </div>
  );
}
